import React, { useState } from 'react';
import { Form, DatePicker, Input, Select, Radio, Checkbox, Button, message } from 'antd';

import './styles.css';
import { useArchiveSearch } from '../../helpers/hooks/useArchiveSearch';
import FileTypeSegmented from './components/FileTypeSegmented';
import { mockCities, mockStreets, ratingOptions } from './consts/consts';
import { useFilesStore } from '../../../../../../../store/devices/FilesDevicesFromDB';
import usePTTMessageStore from '../../../../../pttDispatcher/api/usePttMessageStore';

const { Option } = Select;

// Опции для выпадающих списков
interface ArchiveFiltersProps {
    form: any;
    onFinish: (values: any) => void;
    loading: boolean;
    archiveSearch: ReturnType<typeof useArchiveSearch>;
}

const SearchTypeHint: React.FC<{ searchType: string }> = ({ searchType }) => (
    <div
        style={{
            padding: '16px',
            backgroundColor: 'var(--tertiary-shadow)',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid var(--primary)',
            borderLeft: '4px solid var(--primary)',
        }}
    >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '18px', marginRight: '10px', color: 'var(--primary-dark)' }}>
                🔍
            </span>
            <span className="title small" style={{ color: 'var(--primary-dark)' }}>
                Тип поиска:
            </span>
        </div>
        <span className="body medium" style={{ color: 'var(--gray-black)', fontWeight: 500 }}>
            {searchType}
        </span>
    </div>
);

const ArchiveFilters: React.FC<ArchiveFiltersProps> = ({
    form,
    onFinish,
    loading,
    archiveSearch,
}) => {
    const {
        contentType,
        fileType,
        groups,
        owners,
        pttGroups,
        operators,
        allDevices,
        users,
        setContentType,
        setRecipientType,
        setAuthorType,
        setFileType,
        onReset,
    } = archiveSearch;

    const { resetArchiveFiles } = useFilesStore();

    const { clearArchiveMessages } = usePTTMessageStore();
    const [searchType, setSearchType] = useState<string>('');

    const updateSearchType = () => {
        const values = form.getFieldsValue();
        const hasGroup = !!values.audioRoom;
        const hasOperator = !!values.operatorAuthor;
        const hasDevice = !!values.devicePttAuthor;

        if (hasGroup && hasOperator && hasDevice) {
            setSearchType('Переписка в группе между оператором и устройством');
        } else if (hasGroup && hasOperator) {
            setSearchType('Все сообщения с оператором в группе');
        } else if (hasGroup && hasDevice) {
            setSearchType('Все сообщения с устройством в группе');
        } else if (hasOperator && hasDevice) {
            setSearchType('Личные сообщения между оператором и устройством');
        } else if (hasGroup) {
            setSearchType('Все сообщения в группе');
        } else if (hasOperator) {
            setSearchType('Все сообщения с оператором');
        } else if (hasDevice) {
            setSearchType('Все сообщения с устройством');
        } else {
            setSearchType('Все сообщения (без фильтров)');
        }
    };

    const handleFieldChange = (fieldName: string, value: any) => {
        form.setFieldsValue({ [fieldName]: value });
        updateSearchType();
    };

    const filterOption = (input: string, option: any) => {
        if (!input) return true;

        // Для обычных строковых опций
        if (typeof option.children === 'string') {
            return option.children.toLowerCase().includes(input.toLowerCase());
        }

        // Для owners
        if (owners) {
            const owner = owners.find((o) => o.UID === option.value);
            if (owner?.name) {
                return owner.name.toLowerCase().includes(input.toLowerCase());
            }
        }

        // Для operators
        if (operators) {
            const operator = operators.find((o) => o.uid === option.value);
            if (operator?.name) {
                return operator.name.toLowerCase().includes(input.toLowerCase());
            }
        }

        // Для pttGroups
        if (pttGroups) {
            const group = pttGroups.find((g) => g.uid === option.value);
            if (group?.name) {
                return group.name.toLowerCase().includes(input.toLowerCase());
            }
        }

        if (users) {
            const user = users.find((user) => user.uid === option.value);
            if (user?.name) {
                return user.name.toLowerCase().includes(input.toLowerCase());
            }
        }

        if (allDevices) {
            const device = allDevices.find((device) => device.UID === option.value);
            if (device?.name) {
                return device.name.toLowerCase().includes(input.toLowerCase());
            }
        }

        return false;
    };

    const handlePttGroupChange = (value: string) => {
        let group;
        // pttGroups.map((g) => {
        //     group = g.sub_groups.find((group) => group.uid === value);
        // });
        console.log(group);

        // setSelectedGroup(group || null);
    };

    const handleGroupChange = (value: string) => {
        let group;
        groups.map((g) => {
            group = g.sub_groups.find((group) => group.uid === value);
        });
        console.log(group);
    };

    const handleOwnerChange = (value: string) => {
        const owner = owners.find((o) => o.UID === value);
        console.log(owner);
    };

    const handleAuthorTypeChange = (e: any) => {
        const newAuthorType = e.target.value;
        setAuthorType(newAuthorType);
        // Автоматически устанавливаем противоположный тип для получателя
        setRecipientType(newAuthorType === 1 ? 2 : 1);
    };

    const handleRecipientTypeChange = (e: any) => {
        const newRecipientType = e.target.value;
        setRecipientType(newRecipientType);
        // Автоматически устанавливаем противоположный тип для отправителя
        setAuthorType(newRecipientType === 1 ? 2 : 1);
    };

    const handleReset = () => {
        form.resetFields();
        setSearchType('');
        onReset();
    };

    const validateRadiochatFilters = (values: any): string | null => {
        const hasGroup = !!values.audioRoom;
        const hasOperator = !!values.operatorAuthor;
        const hasDevice = !!values.devicePttAuthor;

        // Если выбраны оператор и устройство без группы - это личная переписка (корректно)
        if (hasOperator && hasDevice && !hasGroup) {
            return null;
        }

        // Если выбрана группа с кем-то - корректно
        if (hasGroup && (hasOperator || hasDevice)) {
            return null;
        }

        // Одиночные фильтры - корректно
        if (hasGroup || hasOperator || hasDevice) {
            return null;
        }

        // Без фильтров - тоже корректно
        return null;
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const validationError = validateRadiochatFilters(values);
            if (validationError) {
                message.warning(validationError);
                return;
            }

            await onFinish(values);
        } catch (error) {
            console.error('Ошибка валидации:', error);
        }
    };

    return (
        <div className={'container-form-archive'}>
            <Form form={form} onFinish={onFinish} className={'archive-form'}>
                {/* Период записи */}
                <div>
                    <span className={'title medium'} style={{ color: 'var(--gray-black)' }}>
                        Период записи
                    </span>
                    <div
                        style={{
                            paddingBottom: 8,
                            marginTop: 16,
                            borderBottom: '2px solid var(--divider-2)',
                            marginBottom: 16,
                        }}
                    >
                        <Form.Item
                            name="fromDate"
                            style={{ flex: 1, marginBottom: 0 }}
                            label={
                                <span
                                    className={'title medium'}
                                    style={{ color: 'var(--gray-black)', width: 22 }}
                                >
                                    С
                                </span>
                            }
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                placeholder="Введите дату начала"
                                showTime={{ format: 'HH:mm:ss' }}
                                format="YYYY-MM-DD HH:mm:ss"
                            />
                        </Form.Item>
                        <Form.Item
                            name="toDate"
                            style={{ flex: 1, marginBottom: 0 }}
                            label={
                                <span
                                    className={'title medium'}
                                    style={{ color: 'var(--gray-black)' }}
                                >
                                    До
                                </span>
                            }
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                placeholder="Введите дату конца"
                                showTime={{ format: 'HH:mm:ss' }}
                                format="YYYY-MM-DD HH:mm:ss"
                            />
                        </Form.Item>
                    </div>
                </div>

                {/* Файлы/Радиочат */}
                <div>
                    <div
                        style={{
                            display: 'flex',
                            borderBottom: '2px solid var(--divider-2)',
                            marginBottom: 16,
                        }}
                    >
                        <div
                            style={{
                                cursor: 'pointer',
                                position: 'relative',
                                paddingBottom: 16,
                                borderBottom:
                                    contentType === 'files' ? '4px solid var(--tertiary)' : 'none',
                                top: 2,
                            }}
                            onClick={() => {
                                setContentType('files');
                                archiveSearch.setPagination((prev) => ({
                                    ...prev,
                                    current: 1,
                                    pageSize: 50,
                                    total: 0,
                                }));
                                clearArchiveMessages();
                            }}
                            className={'headline small'}
                        >
                            Файлы
                        </div>
                        <div
                            style={{
                                marginLeft: '12px',
                                cursor: 'pointer',
                                position: 'relative',
                                top: 2,
                                paddingBottom: 16,
                                borderBottom:
                                    contentType === 'radiochat'
                                        ? '4px solid var(--tertiary)'
                                        : 'none',
                            }}
                            onClick={() => {
                                setContentType('radiochat');
                                archiveSearch.setPagination((prev) => ({
                                    ...prev,
                                    current: 1,
                                    pageSize: 50,
                                    total: 0,
                                }));
                                resetArchiveFiles();
                            }}
                            className={'headline small'}
                        >
                            Радиочат
                        </div>
                    </div>

                    {contentType === 'files' ? (
                        <>
                            <div>
                                <span
                                    className={'title medium'}
                                    style={{ marginBottom: 16, color: 'var(--gray-black)' }}
                                >
                                    Регистратор
                                </span>
                                <div
                                    style={{
                                        paddingBottom: 8,
                                        marginTop: 16,
                                        borderBottom: '2px solid var(--divider-2)',
                                        marginBottom: 16,
                                    }}
                                >
                                    <Form.Item
                                        name="device"
                                        style={{ marginBottom: 16 }}
                                        label={
                                            <span
                                                className={'title medium'}
                                                style={{ color: 'var(--gray-black)' }}
                                            >
                                                Устройство
                                            </span>
                                        }
                                    >
                                        <Select
                                            placeholder="Введите название или выб."
                                            onChange={handleGroupChange}
                                            loading={loading}
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={filterOption}
                                            allowClear={true}
                                        >
                                            {allDevices?.map((device) => (
                                                <Option key={device?.UID} value={device?.UID}>
                                                    {device?.name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>

                                    <Form.Item
                                        name="group"
                                        style={{ marginBottom: 16 }}
                                        label={
                                            <span
                                                className={'title medium'}
                                                style={{ color: 'var(--gray-black)' }}
                                            >
                                                Группа
                                            </span>
                                        }
                                    >
                                        <Select
                                            placeholder="Введите название или выб."
                                            onChange={handleGroupChange}
                                            loading={loading}
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={filterOption}
                                            allowClear={true}
                                        >
                                            {groups?.map((group) =>
                                                group.sub_groups.map((group) => (
                                                    <Option key={group?.uid} value={group?.uid}>
                                                        {group?.name}
                                                    </Option>
                                                )),
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </div>
                            <FileTypeSegmented
                                value={fileType}
                                onChange={setFileType}
                                className={'file-type-segmented-archive'}
                            />
                            <Form.Item
                                name="ratings"
                                labelCol={{ span: 24 }}
                                label={
                                    <span
                                        className={'title medium'}
                                        style={{
                                            color: 'var(--gray-black)',
                                            marginTop: 20,
                                        }}
                                    >
                                        Секретность
                                    </span>
                                }
                            >
                                <Checkbox.Group
                                    options={ratingOptions}
                                    onChange={(checkedValues) =>
                                        form.setFieldsValue({ ratings: checkedValues })
                                    }
                                    style={{ width: 350, marginTop: 8 }}
                                />
                            </Form.Item>
                            <Form.Item
                                name="creator"
                                style={{ marginTop: 40 }}
                                label={
                                    <span
                                        className={'title medium'}
                                        style={{ color: 'var(--gray-black)' }}
                                    >
                                        Автор
                                    </span>
                                }
                            >
                                <Select
                                    placeholder="Введите название или выб."
                                    onChange={handleGroupChange}
                                    loading={loading}
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={filterOption}
                                    allowClear={true}
                                >
                                    {users?.map((user) => (
                                        <Option key={user?.uid} value={user?.name}>
                                            {user?.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                label={
                                    <span
                                        className={'title medium'}
                                        style={{ color: 'var(--gray-black)' }}
                                    >
                                        Номер
                                    </span>
                                }
                                name="caseNumber"
                                // rules={[{ required: true, message: 'Введите номер.' }]}
                                required={false}
                            >
                                <Input
                                    type="text"
                                    placeholder={'Номер'}
                                    onChange={(e) => {
                                        // Разрешаем только цифры
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        form.setFieldsValue({ caseNumber: value });
                                    }}
                                />
                            </Form.Item>
                        </>
                    ) : (
                        // <>
                        //     <Form.Item
                        //         name="audioRoom"
                        //         style={{ marginBottom: 16 }}
                        //         label={
                        //             <span
                        //                 className={'title medium'}
                        //                 style={{ color: 'var(--gray-black)' }}
                        //             >
                        //                 Аудиокомната
                        //             </span>
                        //         }
                        //     >
                        //         <Select
                        //             placeholder="Введите название или выб."
                        //             showSearch
                        //             optionFilterProp="children"
                        //             filterOption={filterOption}
                        //             style={{ width: '100%' }}
                        //             allowClear={true}
                        //         >
                        //             {pttGroups?.map((group) => (
                        //                 <Option key={group?.uid} value={group?.uid}>
                        //                     {group?.name}
                        //                 </Option>
                        //             ))}
                        //         </Select>
                        //     </Form.Item>
                        //
                        //     {/*<div*/}
                        //     {/*    className={'title medium'}*/}
                        //     {/*    style={{ color: 'var(--gray-black)', marginBottom: 8 }}*/}
                        //     {/*>*/}
                        //     {/*    Тип сообщения*/}
                        //     {/*</div>*/}
                        //     {/*<Radio.Group*/}
                        //     {/*    style={{*/}
                        //     {/*        display: 'flex',*/}
                        //     {/*        flexDirection: 'column',*/}
                        //     {/*        borderBottom: '2px solid var(--divider-2)',*/}
                        //     {/*        marginBottom: 7,*/}
                        //     {/*        paddingBottom: 15,*/}
                        //     {/*    }}*/}
                        //     {/*    value={messageType}*/}
                        //     {/*    onChange={(e) => setMessageType(e.target.value)}*/}
                        //     {/*>*/}
                        //     {/*    <Radio value={1}>*/}
                        //     {/*        <span*/}
                        //     {/*            className={'body medium'}*/}
                        //     {/*            style={{ color: 'var(--gray-black)' }}*/}
                        //     {/*        >*/}
                        //     {/*            личные сообщения*/}
                        //     {/*        </span>*/}
                        //     {/*    </Radio>*/}
                        //     {/*    <Radio value={2}>*/}
                        //     {/*        <span*/}
                        //     {/*            className={'body medium'}*/}
                        //     {/*            style={{ color: 'var(--gray-black)' }}*/}
                        //     {/*        >*/}
                        //     {/*            в группу*/}
                        //     {/*        </span>*/}
                        //     {/*    </Radio>*/}
                        //     {/*</Radio.Group>*/}
                        //
                        //     {/*<div*/}
                        //     {/*    className={'title medium'}*/}
                        //     {/*    style={{ color: 'var(--gray-black)', marginBottom: 8 }}*/}
                        //     {/*>*/}
                        //     {/*    Отправитель*/}
                        //     {/*</div>*/}
                        //     {/*<Radio.Group*/}
                        //     {/*    style={{*/}
                        //     {/*        display: 'flex',*/}
                        //     {/*        flexDirection: 'column',*/}
                        //     {/*        marginBottom: 8,*/}
                        //     {/*    }}*/}
                        //     {/*    value={authorType}*/}
                        //     {/*    onChange={handleAuthorTypeChange}*/}
                        //     {/*>*/}
                        //     {/*    <Radio value={1}>*/}
                        //     {/*        <span*/}
                        //     {/*            className={'body medium'}*/}
                        //     {/*            style={{ color: 'var(--gray-black)' }}*/}
                        //     {/*        >*/}
                        //     {/*            оператор*/}
                        //     {/*        </span>*/}
                        //     {/*    </Radio>*/}
                        //     {/*    <Radio value={2}>*/}
                        //     {/*        <span*/}
                        //     {/*            className={'body medium'}*/}
                        //     {/*            style={{ color: 'var(--gray-black)' }}*/}
                        //     {/*        >*/}
                        //     {/*            устройство*/}
                        //     {/*        </span>*/}
                        //     {/*    </Radio>*/}
                        //     {/*</Radio.Group>*/}
                        //     {/*{authorType == 1 ? (*/}
                        //     {/*    <Form.Item*/}
                        //     {/*        name="operatorAuthor"*/}
                        //     {/*        style={{ marginBottom: 0 }}*/}
                        //     {/*        label={*/}
                        //     {/*            <span*/}
                        //     {/*                className={'title medium'}*/}
                        //     {/*                style={{ color: 'var(--gray-black)' }}*/}
                        //     {/*            >*/}
                        //     {/*                Оператор*/}
                        //     {/*            </span>*/}
                        //     {/*        }*/}
                        //     {/*    >*/}
                        //     {/*        <Select*/}
                        //     {/*            placeholder="Введите название или выб."*/}
                        //     {/*            popupClassName="custom-scroll-select"*/}
                        //     {/*            showSearch*/}
                        //     {/*            optionFilterProp="children"*/}
                        //     {/*            filterOption={filterOption}*/}
                        //     {/*            style={{ width: '100%' }}*/}
                        //     {/*            allowClear={true}*/}
                        //     {/*        >*/}
                        //     {/*            {operators?.map((operator) => (*/}
                        //     {/*                <Option key={operator?.uid} value={operator?.uid}>*/}
                        //     {/*                    {operator?.name}*/}
                        //     {/*                </Option>*/}
                        //     {/*            ))}*/}
                        //     {/*        </Select>*/}
                        //     {/*    </Form.Item>*/}
                        //     {/*) : (*/}
                        //     {/*    <Form.Item*/}
                        //     {/*        name="devicePttAuthor"*/}
                        //     {/*        style={{ marginBottom: 16 }}*/}
                        //     {/*        label={*/}
                        //     {/*            <span*/}
                        //     {/*                className={'title medium'}*/}
                        //     {/*                style={{ color: 'var(--gray-black)' }}*/}
                        //     {/*            >*/}
                        //     {/*                Устройство*/}
                        //     {/*            </span>*/}
                        //     {/*        }*/}
                        //     {/*    >*/}
                        //     {/*        <Select*/}
                        //     {/*            placeholder="Введите название или выб."*/}
                        //     {/*            onChange={handleGroupChange}*/}
                        //     {/*            loading={loading}*/}
                        //     {/*            showSearch*/}
                        //     {/*            optionFilterProp="children"*/}
                        //     {/*            filterOption={filterOption}*/}
                        //     {/*            allowClear={true}*/}
                        //     {/*        >*/}
                        //     {/*            {allDevices?.map((device) => (*/}
                        //     {/*                <Option key={device?.UID} value={device?.UID}>*/}
                        //     {/*                    {device?.name}*/}
                        //     {/*                </Option>*/}
                        //     {/*            ))}*/}
                        //     {/*        </Select>*/}
                        //     {/*    </Form.Item>*/}
                        //     {/*)}*/}
                        //     {/*<div*/}
                        //     {/*    className={'title medium'}*/}
                        //     {/*    style={{*/}
                        //     {/*        color: 'var(--gray-black)',*/}
                        //     {/*        marginBottom: 8,*/}
                        //     {/*        borderTop: '2px solid var(--divider-2)',*/}
                        //     {/*        paddingTop: 7,*/}
                        //     {/*    }}*/}
                        //     {/*>*/}
                        //     {/*    Получатель*/}
                        //     {/*</div>*/}
                        //     {/*<Radio.Group*/}
                        //     {/*    style={{*/}
                        //     {/*        display: 'flex',*/}
                        //     {/*        flexDirection: 'column',*/}
                        //     {/*        marginBottom: 8,*/}
                        //     {/*    }}*/}
                        //     {/*    value={recipientType}*/}
                        //     {/*    onChange={handleRecipientTypeChange}*/}
                        //     {/*>*/}
                        //     {/*    <Radio value={1}>*/}
                        //     {/*        <span*/}
                        //     {/*            className={'body medium'}*/}
                        //     {/*            style={{ color: 'var(--gray-black)' }}*/}
                        //     {/*        >*/}
                        //     {/*            оператор*/}
                        //     {/*        </span>*/}
                        //     {/*    </Radio>*/}
                        //     {/*    <Radio value={2}>*/}
                        //     {/*        <span*/}
                        //     {/*            className={'body medium'}*/}
                        //     {/*            style={{ color: 'var(--gray-black)' }}*/}
                        //     {/*        >*/}
                        //     {/*            устройство*/}
                        //     {/*        </span>*/}
                        //     {/*    </Radio>*/}
                        //     {/*</Radio.Group>*/}
                        //     {/*{recipientType == 1 ? (*/}
                        //     {/*    <Form.Item*/}
                        //     {/*        name="operatorRecipient"*/}
                        //     {/*        style={{ marginBottom: 0 }}*/}
                        //     {/*        label={*/}
                        //     {/*            <span*/}
                        //     {/*                className={'title medium'}*/}
                        //     {/*                style={{ color: 'var(--gray-black)' }}*/}
                        //     {/*            >*/}
                        //     {/*                Оператор*/}
                        //     {/*            </span>*/}
                        //     {/*        }*/}
                        //     {/*    >*/}
                        //     {/*        <Select*/}
                        //     {/*            placeholder="Введите название или выб."*/}
                        //     {/*            popupClassName="custom-scroll-select"*/}
                        //     {/*            showSearch*/}
                        //     {/*            optionFilterProp="children"*/}
                        //     {/*            filterOption={filterOption}*/}
                        //     {/*            style={{ width: '100%' }}*/}
                        //     {/*            allowClear={true}*/}
                        //     {/*        >*/}
                        //     {/*            {operators?.map((operator) => (*/}
                        //     {/*                <Option key={operator?.uid} value={operator?.uid}>*/}
                        //     {/*                    {operator?.name}*/}
                        //     {/*                </Option>*/}
                        //     {/*            ))}*/}
                        //     {/*        </Select>*/}
                        //     {/*    </Form.Item>*/}
                        //     {/*) : (*/}
                        //     {/*    <Form.Item*/}
                        //     {/*        name="devicePttRecipient"*/}
                        //     {/*        style={{ marginBottom: 16 }}*/}
                        //     {/*        label={*/}
                        //     {/*            <span*/}
                        //     {/*                className={'title medium'}*/}
                        //     {/*                style={{ color: 'var(--gray-black)' }}*/}
                        //     {/*            >*/}
                        //     {/*                Устройство*/}
                        //     {/*            </span>*/}
                        //     {/*        }*/}
                        //     {/*    >*/}
                        //     {/*        <Select*/}
                        //     {/*            placeholder="Введите название или выб."*/}
                        //     {/*            onChange={handleGroupChange}*/}
                        //     {/*            loading={loading}*/}
                        //     {/*            showSearch*/}
                        //     {/*            optionFilterProp="children"*/}
                        //     {/*            filterOption={filterOption}*/}
                        //     {/*            allowClear={true}*/}
                        //     {/*        >*/}
                        //     {/*            {allDevices?.map((device) => (*/}
                        //     {/*                <Option key={device?.UID} value={device?.UID}>*/}
                        //     {/*                    {device?.name}*/}
                        //     {/*                </Option>*/}
                        //     {/*            ))}*/}
                        //     {/*        </Select>*/}
                        //     {/*    </Form.Item>*/}
                        //     {/*)}*/}
                        // </>
                        <>
                            {/* Аудиокомната (Группа) */}
                            <Form.Item
                                name="audioRoom"
                                label={
                                    <span
                                        className={'title medium'}
                                        style={{ color: 'var(--gray-black)' }}
                                    >
                                        Аудиокомната
                                    </span>
                                }
                            >
                                <Select
                                    placeholder="Выберите аудиокомнату"
                                    onChange={(value) => handleFieldChange('audioRoom', value)}
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={filterOption}
                                    allowClear={true}
                                >
                                    {pttGroups?.map((group) => (
                                        <Option key={group.uid} value={group.uid}>
                                            {group.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {/* Оператор */}
                            <Form.Item
                                name="operatorAuthor"
                                label={
                                    <span
                                        className={'title medium'}
                                        style={{ color: 'var(--gray-black)' }}
                                    >
                                        Оператор
                                    </span>
                                }
                            >
                                <Select
                                    placeholder="Выберите оператора"
                                    onChange={(value) => handleFieldChange('operatorAuthor', value)}
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={filterOption}
                                    allowClear={true}
                                >
                                    {users?.map((operator) => (
                                        <Option key={operator.uid} value={operator.uid}>
                                            {operator.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {/* Устройство */}
                            <Form.Item
                                name="devicePttAuthor"
                                label={
                                    <span
                                        className={'title medium'}
                                        style={{ color: 'var(--gray-black)' }}
                                    >
                                        Устройство
                                    </span>
                                }
                            >
                                <Select
                                    placeholder="Выберите устройство"
                                    onChange={(value) =>
                                        handleFieldChange('devicePttAuthor', value)
                                    }
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={filterOption}
                                    allowClear={true}
                                >
                                    {allDevices?.map((device) => (
                                        <Option key={device.UID} value={device.UID}>
                                            {device.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {/* Информация о типе поиска */}
                            {searchType && (
                                <div
                                    style={{
                                        padding: '12px',
                                        backgroundColor: 'var(--tertiary-shadow)',
                                        borderRadius: '6px',
                                        marginBottom: '16px',
                                        border: '1px solid var(--primary)',
                                        borderLeft: '4px solid var(--primary)',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: '4px',
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontSize: '16px',
                                                marginRight: '8px',
                                                color: 'var(--primary-dark)',
                                            }}
                                        >
                                            🔍
                                        </span>
                                        <span
                                            className="title small"
                                            style={{ color: 'var(--primary-dark)' }}
                                        >
                                            Тип поиска:
                                        </span>
                                    </div>
                                    <span
                                        className="body medium"
                                        style={{ color: 'var(--gray-black)' }}
                                    >
                                        {searchType}
                                    </span>
                                </div>
                            )}

                            {/* Подсказки по комбинациям */}
                            <div
                                style={{
                                    padding: '24px 12px',
                                    backgroundColor: 'var(--gray-black)',
                                    borderRadius: '6px',
                                    marginBottom: '16px',
                                    border: '1px solid var(--divider-2)',
                                }}
                            >
                                <div
                                    className="title small"
                                    style={{
                                        color: 'var(--gray-04)',
                                        marginBottom: '8px',
                                    }}
                                >
                                    Возможные комбинации:
                                </div>
                                <div
                                    style={{
                                        fontSize: '12px',
                                        lineHeight: '2.0',
                                        color: 'var(--gray-04)',
                                    }}
                                >
                                    • <strong>Ничего не выбрано</strong> - все сообщения (без
                                    фильтров)
                                    <br />• <strong>Только группа</strong> - все сообщения в группе
                                    <br />• <strong>Только оператор</strong> - все сообщения с
                                    оператором
                                    <br />• <strong>Только устройство</strong> - все сообщения с
                                    устройством
                                    <br />• <strong>Группа + оператор</strong> - сообщения в группе
                                    с оператором
                                    <br />• <strong>Группа + устройство</strong> - сообщения в
                                    группе с устройством
                                    <br />• <strong>Оператор + устройство</strong> - личные
                                    сообщения
                                    <br />• <strong>Все три</strong> - переписка в группе
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Form>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0 24px 12px 24px',
                }}
            >
                <Button
                    className={
                        'button-state-danger button-size-medium button-type-primary button-base'
                    }
                    onClick={handleReset}
                    style={{ width: '48%' }}
                >
                    Сброс
                </Button>
                <Button
                    className={'button-size-medium button-type-primary button-base'}
                    onClick={handleSubmit}
                    style={{ width: '48%' }}
                >
                    Поиск
                </Button>
            </div>
        </div>
    );
};

export default ArchiveFilters;
